import { useId } from "react";

// A yes/no answer that can stay unanswered (BRK-S304): unanswered is not No,
// so neither radio starts checked. `help` carries a question's fuller wording,
// such as an ACORD question's form text. ToggleSection asks with it too.
//
// It shows as two joined buttons, Yes and No (BRK-S360; Nav, 2026-10-14:
// "toggles in"), the chosen one tinted. Each button draws its own border:
// grey, or the accent alone on the chosen one, never grey and accent together
// (Nav, 2026-10-14). The second overlaps the first by its border's width, so
// the two share one line between them, and the chosen one sits on top.
//
// Each is still a radio: the input lies over its whole button, unseen, so a
// click lands on the radio itself, the arrow keys move between them, and the
// button shows the radio's focus. Each button is 64px wide at least, 32px
// tall, 44px under a coarse pointer; on a phone the pair fills the column.
//
// `readOnly` shows an answer the page works out for the broker, locked
// (BRK-S505): the chosen one still tinted at full strength, so it reads as
// the answer it is, never as a control waiting on them.

const OPTIONS = [
  { answer: true, text: "Yes" },
  { answer: false, text: "No" },
] as const;

export function YesNo({
  label,
  help,
  value,
  onChange,
  error,
  disabled,
  readOnly,
}: {
  label: string;
  help?: string;
  value: boolean | null | undefined;
  onChange?: (next: boolean) => void;
  error?: string;
  disabled?: boolean;
  readOnly?: boolean;
}) {
  const name = useId();
  const labelId = useId();
  const helpId = useId();
  const errorId = useId();
  const describedBy =
    [help ? helpId : null, error ? errorId : null].filter(Boolean).join(" ") ||
    undefined;
  return (
    <div
      role="radiogroup"
      aria-labelledby={labelId}
      aria-describedby={describedBy}
      aria-invalid={error ? true : undefined}
      aria-readonly={readOnly ? true : undefined}
      className="flex flex-col gap-2"
    >
      <span id={labelId} className="text-body font-medium text-brk-text">
        {label}
      </span>
      {help && (
        <p id={helpId} className="text-meta text-brk-dim">
          {help}
        </p>
      )}
      <div
        data-yes-no
        className="grid w-full grid-cols-2 sm:inline-grid sm:w-auto sm:self-start"
      >
        {OPTIONS.map(({ answer, text }, i) => {
          const chosen = value === answer;
          return (
            <label
              key={text}
              data-checked={chosen ? "true" : undefined}
              className={`relative flex h-8 min-w-16 items-center justify-center border px-4 text-meta font-medium has-[:focus-visible]:z-20 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:-outline-offset-2 has-[:focus-visible]:outline-brk-accent [@media(pointer:coarse)]:h-11 ${
                i === 0 ? "rounded-l-md" : "-ml-px rounded-r-md"
              } ${
                chosen
                  ? "z-10 border-brk-accent bg-brk-accent/10 text-brk-accent-strong"
                  : `border-brk-border bg-brk-panel text-brk-text ${readOnly ? "" : "hover:bg-brk-bg"}`
              } ${readOnly ? "cursor-default" : disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
            >
              <input
                type="radio"
                name={name}
                checked={chosen}
                onChange={() => onChange?.(answer)}
                disabled={disabled || readOnly}
                className="absolute inset-0 m-0 h-full w-full cursor-[inherit] appearance-none opacity-0"
              />
              {text}
            </label>
          );
        })}
      </div>
      {error && (
        <p id={errorId} className="text-meta text-brk-danger">
          {error}
        </p>
      )}
    </div>
  );
}
